import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Avatar } from '@/UI/Avatar/Avatar';
import { TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FPL_ICON_COLOR_VAR } from '@/UI/Icons/fplIcon';
import { I18N_KEYS } from '@/language/keys';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { Dropdown } from '@/UI/Dropdown/Dropdown';
import { Input } from '@/UI/Input/Input';
import { AppCheckbox } from '@/UI/AppCheckbox/AppCheckbox';
import { Link } from '@/UI/Link/Link';
import { Trans } from '@/UI/Trans/Trans';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { FigmaDesignLogo16 } from '@/UI/Icons/FigmaDesignLogo';
import { FieldLabelRow } from './PublishToCommunityModalDescription';
import CommunityHandleSection, { type HandleStatus } from './CommunityHandleSection';
import type { PublisherProfile } from '@/api/workflowCommunityPublishing.api';
import type { OrgPublishDestination } from '@/hooks/useOrgPublishDestinations';

interface PublishToCommunityModalDetailsProps {
  supportEmail: string;
  onSupportEmailChange: (v: string) => void;
  agreedToTerms: boolean;
  /** False once the publisher has accepted the Creator Agreement under the user-level rollout. */
  requiresCreatorAgreement: boolean;
  onAgreedToTermsChange: (v: boolean) => void;
  termsError: string | null;
  onTermsClick: () => void;
  profile: PublisherProfile | null;
  userEmail: string;
  userDisplayName: string;
  userAvatarUrl: string;
  handle: string;
  onHandleChange: (v: string) => void;
  onHandleStatusChange: (status: HandleStatus) => void;
  handleError: string | null;
  /** Publishing a tool rather than a workflow. Adds Figma Design to the compatibility list. */
  isPublishingAsTool?: boolean;
  /**
   * An org publish shows only the Organization picker: the support email, Community profile and handle,
   * compatibility and Creator Agreement are Community listing fields the org route neither takes nor needs.
   */
  isOrgPublish?: boolean;
  /** Orgs to choose between on an org publish. */
  orgOptions?: OrgPublishDestination[];
  selectedOrgPlanId?: string;
  onSelectedOrgPlanIdChange?: (planId: string) => void;
}

const PublishToCommunityModalDetails = ({
  supportEmail,
  onSupportEmailChange,
  agreedToTerms,
  requiresCreatorAgreement,
  onAgreedToTermsChange,
  termsError,
  onTermsClick,
  profile,
  userEmail,
  userDisplayName,
  userAvatarUrl,
  handle,
  onHandleChange,
  onHandleStatusChange,
  handleError,
  isPublishingAsTool = false,
  isOrgPublish = false,
  orgOptions = [],
  selectedOrgPlanId,
  onSelectedOrgPlanIdChange,
}: PublishToCommunityModalDetailsProps) => {
  const { t } = useTranslation();
  const orgPickerId = useId();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const cardName = profile?.name || userDisplayName || userEmail;
  const cardAvatar = profile?.img_url || userAvatarUrl;
  const cardEmail = userEmail;

  if (isOrgPublish) {
    return (
      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.PICKER_LABEL)}
          htmlFor={orgPickerId}
        />
        <Dropdown
          id={orgPickerId}
          value={selectedOrgPlanId ?? null}
          options={orgOptions.map((org) => ({ id: org.planId, label: org.name, value: org.planId }))}
          onChange={(opt) => onSelectedOrgPlanIdChange?.(opt.value)}
          width="100%"
          matchTriggerWidth
          sx={{ height: 32 }}
        />
      </FlexCol>
    );
  }

  return (
    <>
      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.EMAIL_LABEL)}
        />
        <Input value={supportEmail} onChange={(e) => onSupportEmailChange(e.target.value)} size="large" fullWidth />
      </FlexCol>

      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.PROFILE_LABEL)}
        />
        <FlexCol
          sx={{
            gap: 2,
            p: 1,
            background: token.bgTransparent,
            border: `1px solid ${token.border}`,
            borderRadius: '5px',
          }}
        >
          <Flex sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Avatar aria-label={cardName} src={cardAvatar} alt={cardName} sx={{ width: 24, height: 24, fontSize: 12 }}>
              {isFplMigrationEnabled ? cardName : cardName.slice(0, 1).toUpperCase()}
            </Avatar>
            <FlexCenVer
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: `1px solid ${token.iconTertiary}`,
                justifyContent: 'center',
              }}
            >
              <FlexCenVer sx={{ width: 7, height: 7, borderRadius: '50%', background: token.iconTertiary }} />
            </FlexCenVer>
          </Flex>
          <FlexCol>
            <TextSmMd color={token.text}>{cardName}</TextSmMd>
            <TextSmRg color={token.textSecondary}>{cardEmail}</TextSmRg>
          </FlexCol>
        </FlexCol>
      </FlexCol>

      {!profile && (
        <CommunityHandleSection
          handle={handle}
          onHandleChange={onHandleChange}
          onStatusChange={onHandleStatusChange}
          submitError={handleError}
        />
      )}

      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.COMPATIBILITY_LABEL)}
        />
        <FlexCenVer sx={{ gap: 1 }}>
          <FlexCenVer
            sx={{
              width: 16,
              height: 16,
              borderRadius: '4px',
              background: color.White100,
              justifyContent: 'center',
              // The glyph sits on a white badge, so it has to be dark. FPL icons take their color
              // from the surrounding context rather than a prop, so the badge declares it.
              ...(isFplMigrationEnabled && { [FPL_ICON_COLOR_VAR]: color.Black100 }),
            }}
          >
            <WeavyLogoFilled
              width={11}
              height={9}
              style={isFplMigrationEnabled ? undefined : { color: color.Black100 }}
            />
          </FlexCenVer>
          <TextSmRg color={token.text}>
            {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.COMPATIBILITY_FIGMA_WEAVE)}
          </TextSmRg>
        </FlexCenVer>

        {/* A tool also runs inside a Figma Design file, from the Tools panel. */}
        {isPublishingAsTool && (
          <FlexCenVer sx={{ gap: 1, mt: 0.5 }}>
            <FigmaDesignLogo16 width={16} height={16} />
            <TextSmRg color={token.text}>
              {t(
                I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.COMPATIBILITY_FIGMA_DESIGN,
              )}
            </TextSmRg>
          </FlexCenVer>
        )}
      </FlexCol>

      <FlexCol sx={{ gap: 1 }}>
        <TextSmRg color={token.textSecondary}>
          {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.TERMS_LABEL)}
        </TextSmRg>
        {requiresCreatorAgreement ? (
          <>
            <FlexCenVer sx={{ gap: 1 }}>
              <AppCheckbox
                size="small"
                checked={agreedToTerms}
                onChange={(_, checked) => onAgreedToTermsChange(checked)}
                inputProps={{
                  'aria-label': `${t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.TERMS_AGREE_PREFIX)} ${t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.TERMS_LINK)}`,
                }}
              />
              <TextSmRg color={token.textSecondary}>
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.TERMS_AGREE_PREFIX)}{' '}
                <TextSmMd
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={onTermsClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onTermsClick();
                    }
                  }}
                  sx={{ color: token.text, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.TERMS_LINK)}
                </TextSmMd>
              </TextSmRg>
            </FlexCenVer>
            {termsError && <TextSmRg color={token.textDanger}>{termsError}</TextSmRg>}
          </>
        ) : (
          <TextSmRg color={token.textSecondary}>
            <Trans
              i18nKey={I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.TERMS_REMINDER}
              components={{
                agreement: (
                  <Link
                    href={EXTERNAL_LINKS.figmaCommunityTerms}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: token.text, textDecoration: 'underline' }}
                  />
                ),
              }}
            />
          </TextSmRg>
        )}
      </FlexCol>
    </>
  );
};

export default PublishToCommunityModalDetails;
